/**
 * @fileoverview Prompt template for storyline-ideation-v1.
 *
 * Qualitative domain knowledge (what a storyline IS, the A/B/C categories,
 * structural model, beat field requirements) comes from
 * `shared/narrative-element-definitions.ts`. Per-storyType beat-density and
 * storyline-count guidance are selected at render time by the
 * `[[[storylineBeatGuidance]]]` and `[[[storylineCountGuidance]]]` branching
 * macros (see `src/ai/agents/branching-macros.ts`). Flash Fiction / Short
 * Story fall through to Novel-level guidance; Novel is the ultimate fallback.
 */

import {
  STRUCTURE_PHASES,
  DUAL_PHASE_MODEL,
  STORYLINE_DEFINITION,
  STORYLINE_BEAT_REQUIREMENTS,
  ABC_CATEGORIES,
  NARRATIVE_ELEMENT_DIFFERENTIATION,
} from '@/ai/agents/shared/narrative-element-definitions';

export const prompt = `
You are an expert narrative architect specializing in beat-based story planning.

## TASK INSTRUCTIONS

Your mission is to create **a single new storyline** for an existing story. You must produce a complete storyline with beats at key structural phases and dramatic story questions.

This storyline must **complement** the existing storylines — avoid duplicating their focus, and look for gaps or opportunities in the current story structure.

### Your Brief

The author has provided the brief for this new storyline below — what they want it to be about, the A/B/C priority it should occupy, and any specific themes, characters, or constraints they want followed. Use this as the authoritative spec: the storyline you produce must match their description and priority. If the brief is sparse, lean on the existing storylines and story context to choose a direction that genuinely fills a gap.

<<<userMessage>>>

---

## STORY CONTEXT

<<<bannedNames>>>

### Existing Storylines

{{#if existingStorylines}}
The story already has these storylines. Your new storyline must complement them — do not duplicate their focus:

{{#each existingStorylines}}
- **{{name}}** ({{priority}}): {{summary}}
{{/each}}
{{else}}
No existing storylines. This will be the first storyline for this story.
{{/if}}

### Primary Context

<<<storyContextLite>>>

<<<loreContext>>>

<<<seriesContext>>>

### Secondary Context

<<<characters>>>

<<<worldElements>>>

[[[planningGenreLens]]]

---

## NARRATIVE STRUCTURE MODEL

${STORYLINE_DEFINITION}

${NARRATIVE_ELEMENT_DIFFERENTIATION}

${STRUCTURE_PHASES}

${DUAL_PHASE_MODEL}

${ABC_CATEGORIES}

[[[storylineCountGuidance]]]

[[[storylineBeatGuidance]]]

${STORYLINE_BEAT_REQUIREMENTS}

---

## OUTPUT INSTRUCTIONS

Your output must be valid JSON matching the required schema. Produce exactly ONE storyline with the fields described in the **Storylines** section above:

- **name**: 3-4 words, evocative — follow the category-specific naming patterns
- **summary**: 3-4 sentences capturing how the storyline starts, develops, and resolves
- **priority**: Must match the user's specified priority
- **beats**: Array of beats at key structural phases — see Beat Field Requirements above
- **storyQuestions**: Array of dramatic questions
`;
